import {
  HttpRequestEntity,
  HttpRequestModelType,
} from 'shared/domain/httpRequest/httpRequestModel';
import { isAbortError } from 'serviceWorker/repository/httpRequest/abortRequest';
import { CONFLICT, FORBIDDEN, NOT_FOUND, UNPROCESSABLE_ENTITY } from 'shared/contants';
import { getErrorCode } from 'shared/utils/errorHandling';
import {
  NON_RETRYABLE_LOCAL_DOCUMENT_UPLOAD_ERROR_CODES,
} from 'shared/utils/errorHandling/constants';
import {
  broadcastCreatedDocumentationError,
  broadcastUpdatedDocumentationError,
  broadcastDocumentationMovedError,
  broadcastDocumentationDeletedError,
  broadcastDocumentationChangedMeanwhile,
} from 'serviceWorker/broadcasts/documentations';
import { broadcastFailUploadedDocument } from 'serviceWorker/broadcasts/documents';
import { hasStatus, isNotFoundOrForbidden } from './helpers';
import { classifyDocumentationConflict, isChangedMeanwhileConflict } from 'shared/domain/documentation/documentationConflict';
import { documentationsSync } from 'serviceWorker/services/documentations';

/**
 * Removes the queued creation of a file (a document, a documentation) the
 * server refused for good: its parent is gone or not accessible, or a limit is
 * reached. Resolves whether it was removed; any other failure is tried again.
 */
export async function removeRefusedFileCreation(
  httpRequests: { remove: (id: number) => Promise<unknown> },
  request: HttpRequestEntity,
  error: unknown
): Promise<boolean> {
  if (!isNotFoundOrForbidden(error) && !hasStatus(error, CONFLICT)) {
    return false;
  }
  await httpRequests.remove(request._id);
  return true;
}

export async function handleNonRetryableDocumentUploadError(
  httpRequests: { remove: (id: number) => Promise<unknown> },
  request: HttpRequestEntity,
  error: unknown
): Promise<boolean> {
  const isDocumentUploadRequest =
    request.method === 'POST' &&
    (request.entityType === HttpRequestModelType.document ||
      request.entityType === HttpRequestModelType.drawing);

  if (!isDocumentUploadRequest) {
    return false;
  }

  const errorCode = getErrorCode(error);
  const isNonRetryableDocumentUploadErrorByCode = Boolean(
    errorCode &&
      NON_RETRYABLE_LOCAL_DOCUMENT_UPLOAD_ERROR_CODES.includes(
        errorCode as (typeof NON_RETRYABLE_LOCAL_DOCUMENT_UPLOAD_ERROR_CODES)[number]
      )
  );

  if (!isNonRetryableDocumentUploadErrorByCode) {
    return false;
  }

  await httpRequests.remove(request._id);
  if (
    request.data &&
    'localId' in request.data &&
    typeof request.data.localId === 'number'
  ) {
    // Mark upload as failed for progress UI without emitting generic error toast.
    broadcastFailUploadedDocument(request.data.localId);
  }
  return true;
}

/** The documentation changes of the queue; a creation is not among them (its refusal is handled above). */
const DOCUMENTATION_CHANGE_TYPES: readonly HttpRequestModelType[] = [
  HttpRequestModelType.documentation,
  HttpRequestModelType.documentationVersion,
  HttpRequestModelType.documentationMove,
  HttpRequestModelType.documentationDelete,
  HttpRequestModelType.documentationRestore,
];

function isDocumentationChange(request: HttpRequestEntity): boolean {
  return request.method !== 'POST' && DOCUMENTATION_CHANGE_TYPES.includes(request.entityType);
}

function isOvertakenDocumentationChange(request: HttpRequestEntity, error: unknown): boolean {
  return isDocumentationChange(request) && isChangedMeanwhileConflict(classifyDocumentationConflict(error));
}

/** Answers after which sending a documentation change again would not save it either. */
const PERMANENT_REFUSAL_STATUSES: readonly number[] = [FORBIDDEN, NOT_FOUND, UNPROCESSABLE_ENTITY];

function isPermanentlyRefusedDocumentationChange(request: HttpRequestEntity, error: unknown): boolean {
  return (
    isOvertakenDocumentationChange(request, error) ||
    (isDocumentationChange(request) && PERMANENT_REFUSAL_STATUSES.some((status) => hasStatus(error, status)))
  );
}

/**
 * Drops a queued documentation change the server refused for good: someone
 * else overtook it (409, PT-7117), it is not allowed, the documentation is gone
 * or the change is invalid. The documentations are downloaded again, so the
 * row shown locally (already changed optimistically) gets back what the server
 * has, and the user can redo the change on it (PT-7305).
 */
export async function removeRefusedDocumentationChange(
  httpRequests: { remove: (id: number) => Promise<unknown> },
  request: HttpRequestEntity,
  error: unknown
): Promise<boolean> {
  if (!isPermanentlyRefusedDocumentationChange(request, error)) {
    return false;
  }
  await httpRequests.remove(request._id);
  documentationsSync.sync();
  return true;
}

export function broadcastDocumentationError(
  request: HttpRequestEntity,
  error: any
): void {
  if (isOvertakenDocumentationChange(request, error)) {
    broadcastDocumentationChangedMeanwhile(error);
    return;
  }
  if (request.method === 'POST' && !isAbortError(error)) {
    broadcastCreatedDocumentationError(error);
  } else if (
    request.method === 'PUT' &&
    request.entityType !== HttpRequestModelType.documentationMove
  ) {
    broadcastUpdatedDocumentationError(error);
  } else if (
    request.method === 'PUT' &&
    request.entityType === HttpRequestModelType.documentationMove
  ) {
    broadcastDocumentationMovedError(error);
  } else if (request.method === 'DELETE') {
    broadcastDocumentationDeletedError(error);
  }
}
